/**
 * Formatting helpers shared by every page.
 *
 * Centralized so "Due Tomorrow, 11:59 PM" reads identically on Classwork, the
 * assignment detail panel, the to-do list, and the calendar. Classroom's whole
 * feel comes from these strings being short, human, and consistent.
 */
import type { CanvasAssignment, CanvasSubmission, GradingType } from './canvas/types';

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

function startOfDay(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

/** Whole calendar days between two dates, ignoring time of day. */
export function daysBetween(a: Date, b: Date): number {
  return Math.round((startOfDay(b).getTime() - startOfDay(a).getTime()) / DAY);
}

const timeFormat = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' });
const shortDate = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });
const shortDateWithYear = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});
const weekday = new Intl.DateTimeFormat(undefined, { weekday: 'long' });
const fullDateTime = new Intl.DateTimeFormat(undefined, {
  weekday: 'long',
  month: 'long',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

export function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Classroom-style due label: "Due Tomorrow, 11:59 PM", "Due Oct 4", "No due date".
 *
 * Deliberately handles the null case as a first-class result rather than as an
 * error, because assignments with no due date are common in Canvas and would
 * otherwise render as "Invalid Date".
 */
export function formatDueDate(value: string | null | undefined, now = new Date()): string {
  const date = parseDate(value);
  if (!date) return 'No due date';

  const diffDays = daysBetween(now, date);

  if (diffDays === 0) return `Due today, ${timeFormat.format(date)}`;
  if (diffDays === 1) return `Due tomorrow, ${timeFormat.format(date)}`;
  if (diffDays === -1) return `Due yesterday, ${timeFormat.format(date)}`;
  if (diffDays > 1 && diffDays < 7) return `Due ${weekday.format(date)}, ${timeFormat.format(date)}`;
  if (date.getFullYear() === now.getFullYear()) return `Due ${shortDate.format(date)}`;
  return `Due ${shortDateWithYear.format(date)}`;
}

/** "in 8 minutes", "3 days ago" — for relative timestamps in the stream. */
export function formatRelative(value: string | null | undefined, now = new Date()): string {
  const date = parseDate(value);
  if (!date) return '';

  const diff = date.getTime() - now.getTime();
  const abs = Math.abs(diff);
  const past = diff < 0;

  const say = (n: number, unit: string) => {
    const plural = n === 1 ? unit : `${unit}s`;
    return past ? `${n} ${plural} ago` : `in ${n} ${plural}`;
  };

  if (abs < MINUTE) return past ? 'just now' : 'in a moment';
  if (abs < HOUR) return say(Math.round(abs / MINUTE), 'minute');
  if (abs < DAY) return say(Math.round(abs / HOUR), 'hour');
  if (abs < 7 * DAY) return say(Math.round(abs / DAY), 'day');
  if (abs < 30 * DAY) return say(Math.round(abs / (7 * DAY)), 'week');
  if (abs < 365 * DAY) return say(Math.round(abs / (30 * DAY)), 'month');
  return say(Math.round(abs / (365 * DAY)), 'year');
}

export function formatDateTime(value: string | null | undefined): string {
  const date = parseDate(value);
  return date ? fullDateTime.format(date) : '—';
}

export function formatShortDate(value: string | null | undefined): string {
  const date = parseDate(value);
  return date ? shortDate.format(date) : '—';
}

/** True when the due date has passed. */
export function isOverdue(value: string | null | undefined, now = new Date()): boolean {
  const date = parseDate(value);
  return date !== null && date.getTime() < now.getTime();
}

/** True when due within the next 24 hours — the "due soon" emphasis. */
export function isDueSoon(value: string | null | undefined, now = new Date()): boolean {
  const date = parseDate(value);
  if (!date) return false;
  const diff = date.getTime() - now.getTime();
  return diff > 0 && diff <= DAY;
}

/* ------------------------------------------------------------------ */
/* Grades                                                              */
/* ------------------------------------------------------------------ */

/**
 * Render a score the way the assignment's grading type says it should read.
 *
 * The five Canvas grading types display the same underlying number very
 * differently, and getting it wrong makes a pass/fail assignment show as
 * "1/1" and a letter-graded exam show as "87" instead of "B+".
 */
export function formatScore(
  submission: Pick<CanvasSubmission, 'score' | 'grade' | 'excused' | 'workflow_state'> | null | undefined,
  assignment?: Pick<CanvasAssignment, 'points_possible' | 'grading_type'> | null,
): string {
  if (!submission) return '—';
  if (submission.excused) return 'Excused';
  if (submission.score === null || submission.score === undefined) {
    return submission.workflow_state === 'submitted' ? 'Turned in' : '—';
  }

  const gradingType: GradingType = assignment?.grading_type ?? 'points';
  const possible = assignment?.points_possible;

  switch (gradingType) {
    case 'pass_fail':
      return submission.grade === 'complete' ? 'Complete' : 'Incomplete';
    case 'letter_grade':
    case 'gpa_scale':
      return submission.grade ?? String(submission.score);
    case 'percent': {
      if (!possible) return `${round(submission.score)}%`;
      return `${round((submission.score / possible) * 100)}%`;
    }
    case 'points':
    default:
      return possible ? `${round(submission.score)}/${round(possible)}` : String(round(submission.score));
  }
}

/** "91.4%" from a Canvas score, or an em dash when there is nothing to show. */
export function formatPercent(score: number | null | undefined, digits = 1): string {
  if (score === null || score === undefined || Number.isNaN(score)) return '—';
  return `${score.toFixed(digits).replace(/\.0+$/, '')}%`;
}

/** Trim pointless trailing zeros: 46.0 -> "46", 46.5 -> "46.5". */
export function round(value: number, digits = 2): string {
  return Number(value.toFixed(digits)).toString();
}

export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}

/** Initials for an avatar fallback. Handles single-word and very long names. */
export function initials(name: string | undefined | null): string {
  if (!name) return '?';
  const parts = name
    .replace(/\b(Mr|Mrs|Ms|Dr|Sr|Sra|Srta|Prof|Señora|Señor)\.?\s+/gi, '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0] ?? '').slice(0, 2).toUpperCase();
  return `${parts[0]?.[0] ?? ''}${parts[parts.length - 1]?.[0] ?? ''}`.toUpperCase();
}

/**
 * Decode HTML entities in a plain-text field.
 *
 * Canvas stores assignment titles with entities already encoded ("Compare
 * &amp; Contrast"). Rendering that as text shows the raw entity; rendering it
 * as HTML is an injection risk for a field that should never contain markup.
 * Decoding to text is the correct third option.
 */
export function decodeEntities(text: string): string {
  if (!text.includes('&')) return text;
  const el = document.createElement('textarea');
  el.innerHTML = text;
  return el.value;
}
